import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, ArrowRight, Check, Printer } from 'lucide-react';
import { findEventOrNull } from '@/lib/mock';
import Logo from '@/components/Logo';
import { toast } from 'sonner';
import { toastPlanLimitError } from '@/lib/plan-limit';
import {
  calculatePromoDiscount,
  canPurchaseTicket,
  loadTicketEngine,
  recordPurchase,
  saveTicketEngine,
  syncTicketEngine,
  validatePromoCode,
} from '@/lib/ticket-engine';
import { evaluateRule, fetchPublishedForm, resolveVisibleFields } from '@/lib/form-builder';
import { fetchBadgeTemplate, fetchBadgeDataSources } from '@/lib/badge-template';
import BadgePrintModal from '@/components/BadgePrintModal';
import { useTheme } from '@/components/ThemeProvider';
import { API_BASE } from '@/lib/utils';
import PublicEventBlocked, { getBlockedPresentation, getRegistrationBlockedPresentation } from '@/components/PublicEventBlocked';
import OtpCodeInput from '@/components/OtpCodeInput';
import { bumpEventFunnel } from '@/lib/funnel-beacon';
import { ThermeEventLogo, useThermePageTitle } from '@/pages/public/grandfathered/therme-landing-shared';

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
function formatDateRange(startDate) {
  if (!startDate) return '';
  const d = new Date(startDate + 'T00:00:00');
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS_FULL[d.getMonth()]} from 9:15AM Onwards`;
}

export default function Register({ slugOverride }) {
  const { setTheme } = useTheme();
  const params = useParams();
  // slugOverride is set when this component is mounted under a white-label
  // custom domain (App.js's CustomDomain branch). Route there is `/register`
  // with no :slug param, so useParams.slug is undefined.
  const slug = slugOverride || params.slug;
  // White-label custom domain mounts the event at `/` (no /e/<slug> prefix),
  // so "Back to event" links and post-register navigation should target `/`
  // when slugOverride is set. Canonical mode keeps the existing /e/<slug>.
  const backToEventPath = slugOverride ? '/' : `/e/${slug}`;
  const isThermeEvent = String(slug || '').trim().toLowerCase() === 'thermesggbreak';
  useThermePageTitle(isThermeEvent);
  // Same bootstrap-fetch pattern as EventLanding -- avoid the race where
  // App.js's 4s fallback timer flips ready=true before /api/bootstrap-data
  // finishes loading on slow servers, leaving __AIVORY_BOOTSTRAP__ empty.
  const [bootstrapEvents, setBootstrapEvents] = useState(() => {
    const cached = window.__AIVORY_BOOTSTRAP__?.events;
    return Array.isArray(cached) ? cached : null;
  });
  const [bootstrapLoading, setBootstrapLoading] = useState(() => {
    const cached = window.__AIVORY_BOOTSTRAP__?.events;
    return !Array.isArray(cached);
  });
  const [registerMeta, setRegisterMeta] = useState(null);
  const [registrationOptions, setRegistrationOptions] = useState({ emailVerification: false });
  const [regOtp, setRegOtp] = useState('');
  const [regOtpVerified, setRegOtpVerified] = useState(false);
  const [regOtpBusy, setRegOtpBusy] = useState(false);
  const [registerPayments, setRegisterPayments] = useState(null);
  const [registerMetaReady, setRegisterMetaReady] = useState(false);
  const [registerMetaError, setRegisterMetaError] = useState('');

  // Always re-fetch on mount so admin-side edits (price, date, theme, etc.)
  // are picked up even when App.js's bootstrap was loaded before the change.
  // The cached value still seeds the initial render so there's no blank frame.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/bootstrap-data`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`bootstrap ${res.status}`);
        const payload = await res.json();
        const events = Array.isArray(payload?.data?.events) ? payload.data.events : [];
        if (!cancel) {
          setBootstrapEvents(events);
          window.__AIVORY_BOOTSTRAP__ = window.__AIVORY_BOOTSTRAP__ || {};
          window.__AIVORY_BOOTSTRAP__.events = events;
        }
      } catch {
        if (!cancel && !Array.isArray(bootstrapEvents)) setBootstrapEvents([]);
      } finally {
        if (!cancel) setBootstrapLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancel = false;
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const params = new URLSearchParams();
    if (slug) params.set('slug', slug);
    if (host) params.set('host', host);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/register-meta?${params}`, { cache: 'no-store' });
        if (res.status === 403) {
          const body = await res.json().catch(() => ({}));
          if (!cancel) {
            setRegisterMeta(null);
            setRegisterMetaError(String(body?.detail || 'Registration is not open for this event.'));
          }
          return;
        }
        if (!res.ok) return;
        const payload = await res.json();
        if (!cancel) {
          setRegisterMeta(payload.event);
          setRegistrationOptions(payload.registration || { emailVerification: false });
          setRegisterMetaError('');
        }
      } catch {
        if (!cancel) {
          setRegisterMeta(null);
          setRegisterMetaError('');
        }
      } finally {
        if (!cancel) setRegisterMetaReady(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug]);

  // Bootstrap wins over mock so admin updates (theme, status, etc.) take
  // priority over the dev-fixture events. register-meta overrides isFree (source of truth).
  const e = useMemo(() => {
    const needle = String(slug || '').trim();
    let base = null;
    if (Array.isArray(bootstrapEvents)) {
      base = bootstrapEvents.find(
        (row) => String(row?.slug || '').trim() === needle || String(row?.id || '').trim() === needle,
      );
    }
    if (!base) base = findEventOrNull(needle);
    if (!base && registerMeta) return registerMeta;
    if (!base) return null;
    if (!registerMeta) return base;
    return { ...base, ...registerMeta, isFree: Boolean(registerMeta.isFree) };
  }, [slug, bootstrapEvents, registerMeta]);

  // Apply per-event theme to the live UI when the event resolves so the
  // registration page matches the public landing.
  useEffect(() => {
    const t = String(e?.theme || '').toLowerCase();
    if (t === 'light' || t === 'dark') setTheme(t, true);
  }, [e?.theme, setTheme]);

  useEffect(() => {
    if (!e?.id) return;
    bumpEventFunnel(e.id, 'form_start');
  }, [e?.id]);

  useEffect(() => {
    if (!registerMetaReady) return;
    const url =
      registrationOptions?.backend === 'formsg'
        ? String(registrationOptions?.formUrl || '').trim()
        : '';
    if (url) window.location.replace(url);
  }, [registerMetaReady, registrationOptions]);

  const navigate = useNavigate();
  const [engine, setEngine] = useState(() => (e?.id ? loadTicketEngine(e.id) : { tickets: [], promoCodes: [] }));
  const saleOpen = (t) => {
    const now = Date.now();
    const startsAt = t.startsAt ? new Date(t.startsAt).getTime() : 0;
    const endsAt = t.endsAt ? new Date(t.endsAt).getTime() : Number.MAX_SAFE_INTEGER;
    const cap = Number(t.quota || 0);
    const unlimited = cap <= 0;
    return now >= startsAt && now <= endsAt && (unlimited || Number(t.sold || 0) < cap);
  };
  const tiers = engine.tickets.filter((t) => t.type === 'TIER' && saleOpen(t));
  const tracks = engine.tickets.filter((t) => t.type === 'TRACK' && saleOpen(t));
  const items = engine.tickets.filter((t) => t.type === 'ITEM' && saleOpen(t));
  const allTiers = engine.tickets.filter((t) => t.type === 'TIER');
  const allTracks = engine.tickets.filter((t) => t.type === 'TRACK');
  const allItems = engine.tickets.filter((t) => t.type === 'ITEM');
  const expiredTiers = allTiers.filter((t) => t.endsAt && Date.now() > new Date(t.endsAt).getTime());
  const soldOutTiers = allTiers.filter((t) => {
    const cap = Number(t.quota || 0);
    if (cap <= 0) return false;
    return Number(t.sold || 0) >= cap;
  });
  const [tier, setTier] = useState(tiers[0]?.id ?? '');
  const [selectedTracks, setSelectedTracks] = useState({});
  const [extras, setExtras] = useState({});
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [mobile, setMobile] = useState('');
  const [role, setRole] = useState('');
  const [country, setCountry] = useState('');
  // Free events skip ticket selection entirely. The first user-visible step
  // becomes "Details" -- we initialise the step counter to 2 so the existing
  // step==2 branch in submit() (form validation + register POST) handles it
  // without restructuring the state machine.
  const isFreeEvent = Boolean(registerMeta?.isFree ?? e?.isFree);
  const [step, setStep] = useState(1);
  const [paymentBusy, setPaymentBusy] = useState(false);
  useEffect(() => {
    if (!registerMetaReady) return;
    setStep(isFreeEvent ? 2 : 1);
  }, [isFreeEvent, registerMetaReady]);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoMsg, setPromoMsg] = useState('');
  const [publishedForm, setPublishedForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [doneRegistration, setDoneRegistration] = useState(null);
  const [badgePrintOpen, setBadgePrintOpen] = useState(false);
  const [badgePrintPack, setBadgePrintPack] = useState(null);

  useEffect(() => {
    if (!e?.id) return;
    syncTicketEngine(e.id).then(() => setEngine(loadTicketEngine(e.id)));
  }, [e?.id]);

  useEffect(() => {
    if (!e?.id) return undefined;
    let cancel = false;
    (async () => {
      try {
        const published = await fetchPublishedForm(e.id);
        if (!cancel) setPublishedForm(published);
      } catch {
        if (!cancel) setPublishedForm(null);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [e?.id]);

  useEffect(() => {
    if (!tiers.length) {
      setTier('');
      return;
    }
    const stillValid = tiers.some((t) => t.id === tier);
    if (!stillValid) setTier(tiers[0].id);
  }, [tiers, tier]);

  const tierObj = tiers.find((t) => t.id === tier);
  const dynamicFields = publishedForm?.fields || [];
  const visibleDynamicFields = resolveVisibleFields(dynamicFields, tierObj?.id || tier, tierObj?.name || '', answers);
  const trackTotal = Object.entries(selectedTracks)
    .filter(([, v]) => v)
    .reduce((s, [k]) => s + (tracks.find((a) => a.id === k)?.price || 0), 0);
  const addonsTotal = Object.entries(extras)
    .filter(([, v]) => v)
    .reduce((s, [k]) => s + (items.find((a) => a.id === k)?.price || 0), 0);
  const subtotal = (tierObj?.price || 0) + trackTotal + addonsTotal;
  const discount = calculatePromoDiscount(subtotal, appliedPromo);
  const taxable = Math.max(0, subtotal - discount);
  const total = taxable;
  const merchantFeePct = Number(registerPayments?.merchantFeePercent || 0);
  const merchantFee = merchantFeePct > 0 ? total * (merchantFeePct / 100) : 0;
  const checkoutTotal = total + merchantFee;
  const isFreeOrder = total === 0 || isFreeEvent;
  // For free events the visible stepper collapses to just "Details" (one
  // step), but internally we keep step=2 so the submit() branch matches.
  const stepLabels = isFreeEvent ? ['Details'] : isFreeOrder ? ['Ticket', 'Details'] : ['Ticket', 'Details', 'Payment'];
  const lastStep = isFreeEvent ? 2 : stepLabels.length;
  const stepNumberInUI = (n) => (isFreeEvent ? n - 1 : n);

  const submit = async (ev) => {
    ev.preventDefault();
    if (step === 1) {
      if (isFreeEvent) {
        setStep(2);
        return;
      }
      if (!tier) {
        toast.error('No active tier is available', {
          description: 'All tiers may be expired or sold out. Contact the event organizer.',
        });
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      const errors = {};
      for (const field of visibleDynamicFields) {
        const value = answers[field.id];
        const isRequired =
          Boolean(field.required) ||
          evaluateRule(field.requiredRule, tierObj?.id || tier, tierObj?.name || '', answers);
        if (!isRequired) continue;
        if ((field.type === 'checkbox' || field.type === 'consent') && !value)
          errors[field.id] = `${field.label} is required`;
        else if (
          (field.type === 'multiselect' || field.type === 'checkbox_group') &&
          (!Array.isArray(value) || value.length === 0)
        )
          errors[field.id] = `${field.label} is required`;
        else if (!String(value ?? '').trim()) errors[field.id] = `${field.label} is required`;
      }
      if (!email.trim()) errors.work_email = 'Work email is required';
      const fullName = `${firstName} ${lastName}`.trim();
      if (!fullName) errors.full_name = 'Full name is required';
      setFormErrors(errors);
      if (Object.keys(errors).length) {
        toast.error('Please complete required fields');
        return;
      }
      if (registrationOptions.emailVerification && !regOtpVerified) {
        if (regOtp.length !== 6) {
          setRegOtpBusy(true);
          try {
            const res = await fetch(`${API_BASE}/api/public/register/request-otp`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ eventId: e.id, email: email.trim() }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.detail || 'Could not send verification code');
            toast.success('Verification code sent', { description: 'Check your inbox for a 6-digit code.' });
          } catch (err) {
            toast.error(err?.message || 'Could not send verification code');
          } finally {
            setRegOtpBusy(false);
          }
          toast.message('Enter verification code', { description: 'Confirm your email before continuing.' });
          return;
        }
        setRegOtpBusy(true);
        try {
          const res = await fetch(`${API_BASE}/api/public/register/verify-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ eventId: e.id, email: email.trim(), code: regOtp }),
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body.detail || 'Invalid code');
          setRegOtpVerified(true);
          toast.success('Email verified');
        } catch (err) {
          toast.error(err?.message || 'Invalid verification code');
          return;
        } finally {
          setRegOtpBusy(false);
        }
      }
      if (!isFreeOrder) {
        setStep(3);
        return;
      }
    }
    let paymentReference = null;
    if (step === 3 && !isFreeOrder) {
      setPaymentBusy(true);
      try {
        const piRes = await fetch(`${API_BASE}/api/public/payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            eventId: e.id,
            amount: checkoutTotal * 1.08,
            email: email.trim(),
            redirectUrl: typeof window !== 'undefined' ? window.location.href.split('?')[0] : '',
          }),
        });
        const piBody = await piRes.json().catch(() => ({}));
        if (!piRes.ok) {
          toast.error('Payment could not be started', {
            description: piBody.detail || piRes.statusText || 'Try again or contact the organizer.',
          });
          return;
        }
        paymentReference = piBody.paymentReference;
        if (!paymentReference) {
          toast.error('Payment reference missing', { description: 'Server did not return a payment reference.' });
          return;
        }
        if (piBody.checkoutUrl && typeof window !== 'undefined') {
          try {
            sessionStorage.setItem(
              `aivory.registration.${e.id}`,
              JSON.stringify({ email: email.trim(), tier, answers, paymentReference }),
            );
          } catch {
            /* ignore */
          }
          window.location.href = piBody.checkoutUrl;
          return;
        }
      } catch {
        toast.error('Could not reach payment service');
        return;
      } finally {
        setPaymentBusy(false);
      }
    }
    const chosenTracks = Object.entries(selectedTracks)
      .filter(([, v]) => v)
      .map(([k]) => k);
    const selectedItems = Object.entries(extras)
      .filter(([, v]) => v)
      .map(([k]) => k);
    for (const ticketId of [tier, ...chosenTracks, ...selectedItems].filter(Boolean)) {
      const check = canPurchaseTicket(engine, email, ticketId);
      if (!check.ok) {
        toast.error('Purchase blocked', {
          description: check.reason,
        });
        return;
      }
    }
    const next = recordPurchase(engine, email, [tier, ...chosenTracks, ...selectedItems].filter(Boolean));
    if (appliedPromo) {
      next.promoCodes = (next.promoCodes || []).map((p) =>
        p.id === appliedPromo.id ? { ...p, used: Number(p.used || 0) + 1 } : p,
      );
    }
    setEngine(next);
    const fullName = `${firstName} ${lastName}`.trim() || 'Guest';
    try {
      const res = await fetch(`${API_BASE}/api/events/${e.id}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fullName,
          title,
          email: email.trim(),
          mobile: mobile.trim(),
          company,
          role,
          // Free events have no ticket selector; backend synthesizes a $0
          // "Free registration" ticket when it sees this sentinel.
          ticket: isFreeEvent ? 'free' : tierObj?.id || tierObj?.name || 'Regular',
          country,
          answers,
          paymentReference: paymentReference || undefined,
          verificationCode: registrationOptions.emailVerification ? regOtp : undefined,
        }),
      });
      if (res.status === 402) {
        const errBody = await res.json().catch(() => ({}));
        toast.error('Payment required', {
          description: typeof errBody?.detail === 'string' ? errBody.detail : 'Complete payment before registering.',
        });
        return;
      }
      if (res.status === 403) {
        const errBody = await res.json().catch(() => ({}));
        const detail = typeof errBody?.detail === 'string' ? errBody.detail : errBody?.detail?.message || 'Registration limit reached for this event.';
        toast.error('Registration unavailable', {
          description: `${detail} Organizers: upgrade in Billing.`,
          action: {
            label: 'Billing',
            onClick: () => { window.location.href = '/admin/billing'; },
          },
        });
        return;
      }
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        if (toastPlanLimitError(errBody.detail, res.status)) return;
        toast.error('Order saved locally; server sync failed', {
          description: errBody.detail || res.statusText || 'Check API is running on port 8001.',
        });
        return;
      }
      const body = await res.json().catch(() => ({}));
      const registrationStatus = String(body?.order?.registrationStatus || '').toLowerCase();
      if (registrationStatus === 'pending approval') {
        toast.success('Registration submitted', {
          description: `${e.title} · pending organizer approval`,
        });
      } else {
        toast.success("You're in.", { description: `${e.title} · ${tierObj?.name || 'Ticket'} · receipt sent` });
      }
      if (body?.attendee) {
        setDoneRegistration({
          attendee: body.attendee,
          order: body.order,
          registrationStatus: String(body?.order?.registrationStatus || ''),
        });
        return;
      }
      navigate(backToEventPath);
    } catch {
      toast.error('Order saved locally; could not reach server', {
        description: 'Attendee will appear after API is available or manual import.',
      });
    }
  };

  const openRegistrationBadgePrint = async () => {
    if (!e?.id || !doneRegistration?.attendee) return;
    try {
      const [template, sources] = await Promise.all([fetchBadgeTemplate(e.id), fetchBadgeDataSources(e.id)]);
      const sample = { ...(sources.attendee?.sample || {}), ...doneRegistration.attendee };
      setBadgePrintPack({
        template,
        dataSources: { ...sources, attendee: { ...sources.attendee, sample } },
      });
      setBadgePrintOpen(true);
    } catch {
      toast.error('Could not load badge template', {
        description: 'Try again from the confirmation email or ask the organiser.',
      });
    }
  };

  if (!e) {
    if (bootstrapLoading) {
      return (
        <div
          className="min-h-screen grid place-items-center text-[var(--text-secondary)]"
          style={{ background: 'var(--bg-base)' }}
        >
          Loading event...
        </div>
      );
    }
    return (
      <div
        className="min-h-screen grid place-items-center text-[var(--error)]"
        style={{ background: 'var(--bg-base)' }}
      >
        Event not found.
      </div>
    );
  }

  if (!registerMetaReady) {
    return (
      <div
        className="min-h-screen grid place-items-center text-[var(--text-secondary)]"
        style={{ background: 'var(--bg-base)' }}
      >
        Loading registration...
      </div>
    );
  }

  const formsgUrl =
    registrationOptions?.backend === 'formsg'
      ? String(registrationOptions?.formUrl || '').trim()
      : '';
  if (formsgUrl) {
    return (
      <div
        className="min-h-screen grid place-items-center text-[var(--text-secondary)]"
        style={{ background: 'var(--bg-base)' }}
      >
        Opening FormSG registration…
      </div>
    );
  }

  const registrationBlocked = getRegistrationBlockedPresentation(e, registerMetaError);
  if (registrationBlocked) {
    return (
      <PublicEventBlocked
        title={registrationBlocked.title}
        message={registrationBlocked.message}
        status={registrationBlocked.status}
      />
    );
  }

  return (
    <div
      className="min-h-screen grid lg:grid-cols-12 relative"
      style={{ background: 'var(--bg-base)' }}
      data-testid="register-page"
    >
      {doneRegistration ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/75 backdrop-blur-md"
          data-testid="register-success"
        >
          <div className="max-w-md w-full surface p-8 border border-[var(--border-default)] shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-[var(--primary)]/20 text-[var(--primary)] grid place-items-center mb-4">
              <Check size={24} />
            </div>
            <h2 className="font-heading text-2xl tracking-tight">Thank you for responding.</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-2">
              {String(doneRegistration.registrationStatus || '')
                .toLowerCase()
                .includes('pending')
                ? 'A confirmation will be sent to your email.'
                : 'A confirmation will be sent to your email.'}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {/* <button type="button" className="btn-primary justify-center" onClick={openRegistrationBadgePrint} data-testid="print-registration-badge">
                <Printer size={14} /> Preview &amp; print badge
              </button> */}
              <Link to={backToEventPath} className="btn-secondary justify-center text-center">
                Back to event
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {badgePrintOpen && badgePrintPack && doneRegistration?.attendee ? (
        <BadgePrintModal
          open={badgePrintOpen}
          onClose={() => setBadgePrintOpen(false)}
          eventId={e.id}
          template={badgePrintPack.template}
          dataSources={badgePrintPack.dataSources}
          subjectType="attendee"
          subjectId={doneRegistration.attendee.id}
          eventTitle={e.title}
          footnote={`${tierObj?.name || 'Ticket'} · Order ${doneRegistration.order?.orderId || '—'}`}
        />
      ) : null}

      {/* Left summary */}
      <aside className="lg:col-span-5 bg-[var(--bg-elev)] border-r border-[var(--border-default)] p-6 md:p-8 lg:p-12 flex flex-col lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto">
        <Link to={backToEventPath} className="btn-ghost text-sm w-fit -ml-2">
          <ArrowLeft size={14} /> Back to event
        </Link>
        {isThermeEvent ? (
          <ThermeEventLogo event={e} className="mt-2" />
        ) : e.logo ? (
          <img
            src={e.logo}
            alt={e.title || 'Event logo'}
            className="h-10 w-auto max-w-[200px] object-contain mt-2"
            data-testid="event-logo"
          />
        ) : (
          <Logo />
        )}
        <div className="mt-12">
          <div className="text-overline">You're registering for</div>
          <h1 className="font-heading text-4xl tracking-tight font-medium mt-2 leading-tight">{e.title}</h1>
          <div className="text-sm text-[var(--text-secondary)] mt-2">
            {e.venue} · {formatDateRange(e.startDate)}
          </div>
        </div>

        {isFreeEvent ? (
          <div className="mt-10 surface p-6">
            {/* <div className="text-overline mb-3">Free event</div> */}
            <p className="text-sm text-[var(--text-secondary)]">Fill in your details to join us</p>
          </div>
        ) : (
          <div className="mt-10 surface p-6">
            <div className="text-overline mb-4">Order summary</div>
            <div className="space-y-3 text-sm">
              <Row k={tierObj?.name || 'Regular'} v={`$${tierObj?.price || 0}`} />
              {Object.entries(selectedTracks)
                .filter(([, v]) => v)
                .map(([k]) => {
                  const a = tracks.find((x) => x.id === k);
                  if (!a) return null;
                  return <Row key={k} k={a.name} v={`$${a.price}`} />;
                })}
              {Object.entries(extras)
                .filter(([, v]) => v)
                .map(([k]) => {
                  const a = items.find((x) => x.id === k);
                  if (!a) return null;
                  return <Row key={k} k={a.name} v={`$${a.price}`} />;
                })}
              <div className="border-t border-[var(--border-default)] pt-3 mt-3">
                {appliedPromo ? <Row k={`Promo (${appliedPromo.code})`} v={`-$${discount.toFixed(2)}`} muted /> : null}
                {merchantFee > 0 ? (
                  <Row k={`Platform fee (${merchantFeePct}%)`} v={`$${merchantFee.toFixed(2)}`} muted />
                ) : null}
                <Row k="Tax" v={`$${(checkoutTotal * 0.08).toFixed(2)}`} muted />
                <div className="flex justify-between mt-3 font-heading text-2xl">
                  <span>Total</span>
                  <span className="font-mono">${(checkoutTotal * 1.08).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-auto pt-10 text-[11px] font-mono text-[var(--text-tertiary)] flex items-center gap-2">
          <Lock size={11} /> SECURED BY AIVORYEMS{isFreeEvent ? '' : ' · STRIPE'}
        </div>
      </aside>

      {/* Right form */}
      <main className="lg:col-span-7 p-6 md:p-8 lg:p-16 pb-32 lg:pb-16">
        <div className="max-w-xl">
          {/* Step indicator. Free events render only "Details" so we map
              the internal step value (which stays at 2) onto the single
              visible position via stepNumberInUI(). */}
          <div className="flex items-center gap-2 mb-10">
            {stepLabels.map((s, i) => {
              const uiStep = stepNumberInUI(step);
              return (
                <div key={s} className="flex items-center gap-2 flex-1">
                  <span
                    className={`w-6 h-6 flex items-center justify-center text-xs font-mono ${
                      i + 1 === uiStep
                        ? 'bg-[var(--primary)] text-[var(--on-primary)]'
                        : i + 1 < uiStep
                          ? 'bg-[var(--text-primary)] text-[var(--bg-base)]'
                          : 'border border-[var(--border-default)] text-[var(--text-tertiary)]'
                    }`}
                  >
                    {i + 1 < uiStep ? <Check size={11} /> : i + 1}
                  </span>
                  <span
                    className={`text-xs font-mono uppercase tracking-widest ${i + 1 === uiStep ? 'text-[var(--primary)]' : 'text-[var(--text-tertiary)]'}`}
                  >
                    {s}
                  </span>
                  {i < stepLabels.length - 1 && <div className="flex-1 h-px bg-[var(--border-default)]" />}
                </div>
              );
            })}
          </div>

          <form onSubmit={submit}>
            {step === 1 && !isFreeEvent && (
              <>
                <div className="text-overline">{''}</div>
                <h2 className="font-heading text-3xl mt-2 tracking-tight">Choose your ticket</h2>
                <div className="mt-8 space-y-3">
                  {tiers.map((t) => (
                    <label
                      key={t.id}
                      className={`block surface p-5 cursor-pointer transition ${tier === t.id ? 'ring-1 ring-[var(--primary)] border-[var(--primary)]' : 'hover:border-[var(--border-hover)]'}`}
                      data-testid={`tier-option-${t.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              checked={tier === t.id}
                              onChange={() => setTier(t.id)}
                              className="accent-[var(--primary)]"
                            />
                            <span className="font-heading text-lg">{t.name}</span>
                          </div>
                          <div className="text-xs text-[var(--text-tertiary)] font-mono mt-2 ml-7">
                            {t.startsAt?.slice(0, 10)} → {t.endsAt?.slice(0, 10)}
                          </div>
                        </div>
                        <div className="font-mono text-2xl">${t.price}</div>
                      </div>
                    </label>
                  ))}
                  {!tiers.length ? (
                    <div className="surface p-5 border border-[var(--error)]/40">
                      <div className="font-heading text-xl text-[var(--error)]">No active tiers</div>
                      <div className="text-sm text-[var(--text-secondary)] mt-2">
                        Active tiers are not available right now.
                        {expiredTiers.length ? ` ${expiredTiers.length} tier(s) expired.` : ''}
                        {soldOutTiers.length ? ` ${soldOutTiers.length} tier(s) sold out.` : ''}
                      </div>
                    </div>
                  ) : null}
                </div>

                <h3 className="font-heading text-xl mt-12 mb-4">Tracks</h3>
                <div className="space-y-3">
                  {tracks.map((a) => (
                    <label key={a.id} className="surface p-4 flex items-center justify-between cursor-pointer">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={!!selectedTracks[a.id]}
                          onChange={(ev) => setSelectedTracks({ ...selectedTracks, [a.id]: ev.target.checked })}
                          className="accent-[var(--primary)]"
                        />
                        <span>{a.name}</span>
                      </div>
                      <div className="font-mono">+${a.price}</div>
                    </label>
                  ))}
                  {!tracks.length ? (
                    <div className="text-xs text-[var(--text-tertiary)] surface p-4">
                      {allTracks.length
                        ? 'No active tracks (expired or sold out).'
                        : 'No tracks configured for this event.'}
                    </div>
                  ) : null}
                </div>
                <h3 className="font-heading text-xl mt-12 mb-4">Related items</h3>
                <div className="space-y-3">
                  {items.map((a) => (
                    <label key={a.id} className="surface p-4 flex items-center justify-between cursor-pointer">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={!!extras[a.id]}
                          onChange={(ev) => setExtras({ ...extras, [a.id]: ev.target.checked })}
                          className="accent-[var(--primary)]"
                        />
                        <span>{a.name}</span>
                      </div>
                      <div className="font-mono">+${a.price}</div>
                    </label>
                  ))}
                  {!items.length ? (
                    <div className="text-xs text-[var(--text-tertiary)] surface p-4">
                      {allItems.length
                        ? 'No active items (expired or sold out).'
                        : 'No related items configured for this event.'}
                    </div>
                  ) : null}
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="text-overline">{''}</div>
                {/* <h2 className="font-heading text-3xl mt-2 tracking-tight">Tell us about you</h2> */}
                <div className="mt-8 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="First name">
                      <input
                        className="input-dark"
                        value={firstName}
                        placeholder="John"
                        onChange={(ev) => setFirstName(ev.target.value)}
                      />
                    </Field>
                    <Field label="Last name">
                      <input
                        className="input-dark"
                        value={lastName}
                        placeholder="Doe"
                        onChange={(ev) => setLastName(ev.target.value)}
                      />
                    </Field>
                  </div>
                  <Field label="Title">
                    <input
                      className="input-dark"
                      placeholder="Marketing Manager"
                      type="text"
                      value={title}
                      onChange={(ev) => setTitle(ev.target.value)}
                    />
                  </Field>

                  <Field label="Work email">
                    <input
                      className="input-dark"
                      placeholder="John@acme.com"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Field>
                  <Field label="Company">
                    <input
                      className="input-dark"
                      placeholder="Acme LTD"
                      value={company}
                      onChange={(ev) => setCompany(ev.target.value)}
                    />
                  </Field>
                  <Field label="Mobile number">
                    <input
                      className="input-dark font-mono"
                      placeholder="91234567"
                      type="tel"
                      value={mobile}
                      onChange={(ev) => setMobile(ev.target.value)}
                    />
                  </Field>
                  {/* <div className="grid grid-cols-2 gap-3">
                    <Field label="Role">
                      <select className="input-dark" value={role} onChange={(ev) => setRole(ev.target.value)}>
                        <option value="">Select role</option>
                        <option>Founder/CEO</option>
                        <option>Engineering</option>
                        <option>Product</option>
                      </select>
                    </Field>
                    <Field label="Country">
                      <input className="input-dark font-mono" placeholder="ISO e.g. US" value={country} onChange={(ev) => setCountry(ev.target.value)} />
                    </Field>
                  </div>
                  <Field label="Dietary requirements"><input className="input-dark" placeholder="Optional" /></Field> */}
                  {/* <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                    <input type="checkbox" className="accent-[var(--primary)]" defaultChecked /> I'd like updates about future events
                  </label> */}
                  {visibleDynamicFields.length ? (
                    <div className="pt-2 border-t border-[var(--border-default)]">
                      <div className="text-overline mb-3">Custom registration fields</div>
                      <div className="space-y-4">
                        {visibleDynamicFields.map((f) => (
                          <Field
                            key={f.id}
                            label={`${f.label}${Boolean(f.required) || evaluateRule(f.requiredRule, tierObj?.id || tier, tierObj?.name || '', answers) ? ' *' : ''}`}
                          >
                            <DynamicFieldInput
                              field={f}
                              value={answers[f.id]}
                              onChange={(nextVal) => {
                                setAnswers((prev) => ({ ...prev, [f.id]: nextVal }));
                                setFormErrors((prev) => {
                                  if (!prev[f.id]) return prev;
                                  const cloned = { ...prev };
                                  delete cloned[f.id];
                                  return cloned;
                                });
                              }}
                            />
                            {formErrors[f.id] ? (
                              <p className="text-xs text-[var(--error)] mt-2">{formErrors[f.id]}</p>
                            ) : null}
                            {f.helpText ? (
                              <p className="text-xs text-[var(--text-tertiary)] mt-2">{f.helpText}</p>
                            ) : null}
                          </Field>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {formErrors.work_email ? (
                    <p className="text-xs text-[var(--error)]">{formErrors.work_email}</p>
                  ) : null}
                  {formErrors.full_name ? <p className="text-xs text-[var(--error)]">{formErrors.full_name}</p> : null}
                  {registrationOptions.emailVerification ? (
                    <div className="mt-6 p-4 border border-[var(--border-default)] rounded-lg space-y-3">
                      <div className="text-overline">Verify email</div>
                      <p className="text-xs text-[var(--text-secondary)]">
                        We will send a 6-digit code to confirm your work email before registration is submitted.
                      </p>
                      <OtpCodeInput value={regOtp} onChange={setRegOtp} disabled={regOtpBusy || regOtpVerified} />
                      {regOtpVerified ? (
                        <p className="text-xs text-[var(--primary)]">Email verified</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="text-overline">Step 3</div>
                <h2 className="font-heading text-3xl mt-2 tracking-tight">Payment</h2>
                <div className="mt-8 space-y-4">
                  <Field label="Card number">
                    <input className="input-dark font-mono" placeholder="Card number" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Expiry">
                      <input className="input-dark font-mono" placeholder="MM / YY" />
                    </Field>
                    <Field label="CVC">
                      <input className="input-dark font-mono" placeholder="CVC" />
                    </Field>
                  </div>
                  <Field label="Name on card">
                    <input className="input-dark" placeholder="Cardholder name" />
                  </Field>
                  <Field label="Promo code">
                    <div className="flex gap-2">
                      <input
                        className="input-dark font-mono uppercase flex-1"
                        placeholder="OPTIONAL"
                        value={promoInput}
                        onChange={(ev) => {
                          setPromoInput(ev.target.value);
                          setPromoMsg('');
                        }}
                      />
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          const result = validatePromoCode(engine, promoInput);
                          if (!result.ok) {
                            setAppliedPromo(null);
                            setPromoMsg(result.reason);
                            toast.error('Promo invalid', { description: result.reason });
                            return;
                          }
                          setAppliedPromo(result.promo);
                          setPromoMsg(`Applied ${result.promo.code} (${result.promo.discountPercent}% off)`);
                          toast.success('Promo applied');
                        }}
                      >
                        Apply
                      </button>
                    </div>
                    {promoMsg ? <p className="text-xs text-[var(--text-secondary)] mt-2">{promoMsg}</p> : null}
                  </Field>
                </div>
              </>
            )}

            <div
              className="fixed bottom-0 inset-x-0 lg:static bg-[var(--bg-base-alpha95)] backdrop-blur-md border-t border-[var(--border-default)] p-4 lg:p-0 lg:mt-12 lg:pt-6 lg:bg-transparent lg:backdrop-blur-none flex items-center justify-between z-30"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            >
              {step > 1 && !isFreeEvent ? (
                <button type="button" onClick={() => setStep((s) => s - 1)} className="btn-ghost">
                  <ArrowLeft size={14} /> Back
                </button>
              ) : (
                <span />
              )}
              <button type="submit" className="btn-primary" data-testid="register-continue" disabled={paymentBusy}>
                {paymentBusy
                  ? 'Processing…'
                  : step < lastStep
                    ? 'Continue'
                    : isFreeOrder
                      ? 'Complete Registration'
                      : `Pay $${(checkoutTotal * 1.08).toFixed(2)}`}{' '}
                {!paymentBusy ? <ArrowRight size={14} /> : null}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-overline">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Row({ k, v, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-secondary)]'}>{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}

function DynamicFieldInput({ field, value, onChange }) {
  if (field.type === 'section') {
    return (
      <div className="text-sm text-[var(--text-tertiary)] border-l-2 border-[var(--border-default)] pl-3 py-1">
        {field.helpText || 'Section details'}
      </div>
    );
  }
  if (field.type === 'textarea' || field.type === 'long_answer' || field.type === 'paragraph') {
    return (
      <textarea
        className="input-dark"
        rows={3}
        value={value || ''}
        placeholder={field.placeholder || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.type === 'select' || field.type === 'dropdown') {
    return (
      <select className="input-dark" value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select...</option>
        {(field.options || []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === 'radio' || field.type === 'yes_no') {
    return (
      <div className="space-y-2">
        {(field.options || []).map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <input
              type="radio"
              name={`radio-${field.id}`}
              className="accent-[var(--primary)]"
              checked={value === opt}
              onChange={() => onChange(opt)}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }
  if (
    field.type === 'multiselect' ||
    field.type === 'checkbox_group' ||
    field.type === 'country' ||
    field.type === 'rating' ||
    field.type === 'linear_scale' ||
    field.type === 'nps'
  ) {
    const selected = Array.isArray(value) ? value : [];
    const singleChoice =
      field.type === 'country' || field.type === 'rating' || field.type === 'linear_scale' || field.type === 'nps';
    if (singleChoice) {
      return (
        <select className="input-dark" value={value || ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select...</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    return (
      <div className="space-y-2">
        {(field.options || []).map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <input
              type="checkbox"
              className="accent-[var(--primary)]"
              checked={selected.includes(opt)}
              onChange={(e) => {
                if (e.target.checked) onChange([...selected, opt]);
                else onChange(selected.filter((x) => x !== opt));
              }}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }
  if (field.type === 'checkbox' || field.type === 'consent') {
    return (
      <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
        <input
          type="checkbox"
          className="accent-[var(--primary)]"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        {field.placeholder || 'Confirm'}
      </label>
    );
  }
  if (field.type === 'hidden')
    return (
      <input
        className="input-dark font-mono"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || 'Hidden value'}
      />
    );
  const type =
    field.type === 'email'
      ? 'email'
      : field.type === 'date'
        ? 'date'
        : field.type === 'time'
          ? 'time'
          : field.type === 'datetime'
            ? 'datetime-local'
            : field.type === 'phone'
              ? 'tel'
              : field.type === 'number'
                ? 'number'
                : field.type === 'url'
                  ? 'url'
                  : 'text';
  return (
    <input
      className="input-dark"
      type={type}
      value={value || ''}
      placeholder={field.placeholder || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
